import { useState, useEffect, useRef } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import * as api from '../../api/client'
import EntityPicker, { PickedEntity } from './EntityPicker'
import SearchableSelect from '../../components/common/SearchableSelect'
import DateInput from '../../components/ui/DateInput'
import { facilityOption } from './facilityOption'
import LabPanel from '../../components/carerx/LabPanel'
import LabRequirementDialog, { labReasonLabel, type LabRequirementAction } from '../../components/carerx/LabRequirementDialog'
import { analyteLabel } from '../../lib/analytes'
import { formatUsDate, formatUsDateLocal } from '../../api/format'
import AppButton from '../../components/ui/AppButton'
import { useQueryClient } from '@tanstack/react-query'
import { fetchTherapyLabs, useChangeTherapyLab } from '../../hooks/queries/carerxEnrollmentQueue'
import EnrollmentAdminConfig, { type AdminOverrides } from './EnrollmentAdminConfig'
import PharmacistPicker, { defaultPharmacist } from './PharmacistPicker'
import { usePharmacistRoster, useAssignReview } from '../../hooks/queries/carerxClinical'
import { useAuth } from '../../auth/AuthContext'
import { feetInchesFromInches, formatWeightBanner, inchesFromFeetInches, linkedWeightBoxes, measurementPayload, ouncesFromWeightBox, weightBoxFromOunces, type WeightBoxUnit } from '../../lib/measurements'
import { useCareRxWeightUnit } from '../../hooks/queries/pharmacyFeatures'
import LanguageBadge from '../../components/ui/LanguageBadge'
import PatientLanguageBadge from '../../components/ui/PatientLanguageBadge'
import { hasRealAllergies } from '../../components/carerx/allergyText'
import PregnancyStatusField, { usePregnancyRadio } from './PregnancyStatusField'
import { pregnancyNote } from './pregnancyStatus'
import { pickerStartNote } from '../../lib/rxStartDate'
import AttachRxButton from './AttachRxButton'
import { friendlyError } from '../../api/errors'

// Extracted D-CRX-18 enrollment door (D-CRX-21): one implementation, mounted on the PCW workflow,
// the enrollment dashboard, and patient profiles (with preset patient / drug).
const card: React.CSSProperties = { border: '1px solid var(--color-border)', borderRadius: 12, background: 'var(--color-surface)', padding: 14 }
const linkBtn: React.CSSProperties = { fontSize: 12.5, cursor: 'pointer', border: '1px solid var(--color-border)', background: 'var(--color-surface)', color: 'var(--color-text)', borderRadius: 6, padding: '5px 12px' }
// UAT 2026-07-29 #3 (shared with the read-only lock below): dose + frequency parse from the SIG.
const DOSE_RE = /\d+(\.\d+)?\s?(MG|MCG|G|ML|%|UNITS?)(\s?\/\s?KG)?(\s(IV|SC|IM|PO))?/i
const FREQ_RE = /(EVERY\s+\d*\s*(WEEKS?|DAYS?|MONTHS?|HOURS?)|DAILY|WEEKLY|MONTHLY|Q\d+\s?(WK|W|D|H|MO|WEEKS?|DAYS?|MONTHS?))/i
const roInput: React.CSSProperties = { padding: '6px 8px', borderRadius: 6, border: '1px solid var(--color-border)', fontSize: 12.5, background: 'var(--color-surface-2, #f9fafb)' }

const hwInput: React.CSSProperties = { padding: '4px 7px', borderRadius: 6, border: '1px solid var(--color-border)', fontSize: 12, width: 64 }

/** D-CRX-95: a lab on the form — the lab-check's answer, marked when it was added to this enrollment. */
type DoorLab = api.LabCheckItem & { added?: boolean }
type WaivedLab = api.LabRequirementItem & { label?: string; by?: string | null; at?: string | null }

/** The kit's button at the size a lab row needs (E-rules: no hand-rolled buttons). */
const LabAction = ({ children, onClick, title }: { children: React.ReactNode; onClick: () => void; title?: string }) =>
  <AppButton size="small" variant="outlined" onClick={onClick} title={title} sx={{ py: 0.25, px: 1, minWidth: 0, fontSize: 12, textTransform: 'none' }}>{children}</AppButton>
const LAB_SOURCE: Record<api.LabResultSource, string> = { order: 'lab order', screening: 'screening', result: 'Clinical Review' }

/** An existing enrollment's own list, in the form's shape. */
function toDoorLabs(t: Awaited<ReturnType<typeof api.getTherapyLabs>>): { items: DoorLab[]; waived: WaivedLab[] } {
  return {
    items: t.labs.map(l => ({ analyte: l.analyte, last_date: l.evidence_date, current: l.current, result_date: l.result_date,
      result_value: l.result_value, result_source: l.result_source, added: l.source === 'added' })),
    waived: t.waived.map(w => ({ analyte: w.analyte, label: w.label, reason_code: (w.reason_code ?? 'other') as api.LabReasonCode,
      reason_text: w.reason_text ?? undefined, by: w.by, at: w.at })),
  }
}

/** One line per lab: where its date comes from, so "on file" is never a mystery (design §8). */
function labSourceNote(q: DoorLab, reopened: boolean): string | null {
  const parts: string[] = []
  if (q.added) parts.push('added to this enrollment')
  if (q.current && q.last_date) parts.push(reopened ? `on file ${formatUsDate(q.last_date)} — enter the newer draw` : `on file ${formatUsDate(q.last_date)}`)
  if (q.result_date || q.result_value)
    parts.push([q.result_value, q.result_date && q.result_date !== q.last_date && formatUsDate(q.result_date),
      q.result_source && LAB_SOURCE[q.result_source]].filter(Boolean).join(' · '))
  return parts.length ? parts.join(' · ') : null
}

export default function EnrollmentDoor({ onClose, onCreated, presetPatient, presetLanguage, presetFormularyId, presetRxNumber, presetPriority, presetPharmacistId, editTherapy, embedded }: {
  onClose: () => void; onCreated: (r?: { pcw_id: string }) => void;
  presetPatient?: PickedEntity | null; presetFormularyId?: string;
  /** ASP #9: the PA feed's language for a patient not in CareRx yet; omitted → the shared CareRx list. */
  presetLanguage?: string | null;
  /* Owner ruling 2026-10-01: one enrollment per patient per PRESCRIPTION. A PA-approved feed row
     is one Rx, and the door opens with that Rx already anchored. */
  presetRxNumber?: string | null;
  presetPriority?: string;   // D-CRX-45 CR-G2: priority chosen in the PA queue, prefills the door
  /** Owner 2026-10-04: the therapy's current pharmacist (edit mode). */
  presetPharmacistId?: string | null;
  /* UAT 2026-07-29 #4: EDIT mode — reopens an existing enrollment (extras + site of care).
     Drug/Rx anchor stays fixed (drug change = new therapy, D-CRX-1); home meds have their own API. */
  editTherapy?: { therapy_id: string; drug_name: string | null; formulary_id: string | null; site?: string | null };
  /** True when a SidePanel already draws the frame and names the record — drop the own card/title. */
  embedded?: boolean;
}) {
  const isEdit = !!editTherapy

  const [patient, setPatient] = useState<PickedEntity | null>(presetPatient ?? null)
  const [drugs, setDrugs] = useState<{ id: string; name: string; care_plan_frequency?: string | null }[]>([])
  const [formularyId, setFormularyId] = useState(editTherapy?.formulary_id ?? presetFormularyId ?? '')
  // D-CRX-72: protocols / flush / rate rule, defaulted from the drug record and adjustable
  // for this patient at the moment of enrollment.
  const [adminOv, setAdminOv] = useState<AdminOverrides>({})
  const [twins, setTwins] = useState<{ id: string; display_name: string | null; mrn: string | null }[]>([])
  const [twinVerified, setTwinVerified] = useState(false)
  const [dups, setDups] = useState<{ id: string; state: string }[]>([])
  const [overrideReason, setOverrideReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  // D-CRX-87b: the anchored Rx was attached to an existing enrollment instead (combining pharmacies).
  const [attachedTo, setAttachedTo] = useState<{ ref: string; rx: string } | null>(null)
  const [doseSig, setDoseSig] = useState('')
  const [paFu] = useState('')  // G-5: field hidden; state kept for the extras API shape
  const [outreach, setOutreach] = useState('')
  const [notes, setNotes] = useState('')
  const [nextInf, setNextInf] = useState('')
  const [deliverOn, setDeliverOn] = useState('')
  const [eCity, setECity] = useState('')
  const [eZip, setEZip] = useState('')
  const [extrasNote, setExtrasNote] = useState('')
  const [siteOfCare, setSiteOfCare] = useState('')
  // G-34: lab provider selector (fed by the Pharmacy Settings directory, G-33).
  const [labProviders, setLabProviders] = useState<api.LabProvider[]>([])
  const [labProviderId, setLabProviderId] = useState('')
  // D-CRX-42: types + facilities come from Site of Care config (SOC-2) — nothing hard-coded.
  const [socTypes, setSocTypes] = useState<api.SiteOfCareType[]>([])
  const [facilities, setFacilities] = useState<api.FacilityRow[]>([])
  const [facilityId, setFacilityId] = useState('')
  const [insName, setInsName] = useState<string | null>(null)
  const [done, setDone] = useState<{ pcw_id: string; therapy_archetype: string; pcw_existing: boolean; routing?: string; missing_labs?: string[] } | null>(null)
  // D-CRX-42 Slice 1: Rx-anchored picker + Pioneer banner (allergies, height/weight)
  const [rxRows, setRxRows] = useState<api.EnrollRxRow[]>([])
  const [rxBanner, setRxBanner] = useState<api.EnrollPatientBanner | null>(null)
  const [rxNumber, setRxNumber] = useState('')
  // Owner review 2026-08-13 (B-1): the anchor is the PICKED ROW, not its rx_number — demo Rx (and
  // any Pioneer row missing a number) have rx_number NULL, which silently disengaged the Pioneer
  // read-only lock and never showed the radio as selected.
  const [anchored, setAnchored] = useState<api.EnrollRxRow | null>(null)
  const [rxState, setRxState] = useState<'idle' | 'loading' | 'ok' | 'error'>('idle')
  // UAT 2026-07-29: rx-list now returns ALL Rx; PA-approved-only is the default view (mockup radio).
  const [showAllRx, setShowAllRx] = useState(false)
  // D-CRX-87 §2: the pharmacy's Rx start date (null = none); Rx before it are greyed, never hidden.
  const [rxStartDate, setRxStartDate] = useState<string | null>(null)
  // The Rx list was a full-width table of every prescription on file - 41 rows for one patient,
  // pushing the actual enrollment fields off the screen. A pharmacist picks exactly one, so it is a
  // dropdown, and the whole block starts collapsed: the anchor is a Stage-0 decision, not the thing
  // you scroll past on the way to the form.
  const [rxOpen, setRxOpen] = useState(false)
  const [rxErr, setRxErr] = useState('')
  // Review L14: the PA-list prescription this door was opened for could not be anchored — said, not silent.
  const [presetNote, setPresetNote] = useState<string | null>(null)
  const [therapyType, setTherapyType] = useState('New Start')
  // D-CRX-80: operator-measured height/weight, entered only when PioneerRx carries none. Held as
  // strings so an empty box stays empty rather than becoming 0 — a weight of zero is not a blank.
  // ASP #5: height in feet + inches (how it is measured), stored as inches. Seeded from the
  // enrollment snapshot (edit) or PioneerRx (create); SENT only when the operator changes a box, so
  // an untouched prefill is never recorded as an operator measurement in the audit trail.
  const [heightFt, setHeightFt] = useState('')
  const [heightInPart, setHeightInPart] = useState('')
  // D-CRX-88: the weight box is in the pharmacy's unit (lb today, or kg). Until the operator types, the box SHOWS
  // the seeded ounces in the unit in force, so a unit that loads after the seed can never label a pound figure
  // "kg". Once typed, the text keeps the unit it was typed in. Under 'both' there are two linked boxes (kg, lb):
  // the one typed in last is the value saved, and the other shows its stored ounces (linkedWeightBoxes).
  const weightUnit = useCareRxWeightUnit()
  const [weightSeedOz, setWeightSeedOz] = useState<number | null>(null)
  const [weightTyped, setWeightTyped] = useState<{ text: string; unit: WeightBoxUnit } | null>(null)
  // Height and weight are tracked SEPARATELY: editing one must not send the other's untouched
  // prefill, or the audit records a PioneerRx value as an operator measurement (code review, Lucas).
  const [heightDirty, setHeightDirty] = useState(false)
  const [weightDirty, setWeightDirty] = useState(false)
  // The seeds arrive asynchronously (Pioneer banner / enrollment draft). Refs, not the state, so a
  // late-arriving seed can never overwrite what the operator has already typed.
  const heightTouched = useRef(false)
  const weightTouched = useRef(false)
  const touchHeight = () => { heightTouched.current = true; setHeightDirty(true) }
  const touchWeight = () => { weightTouched.current = true; setWeightDirty(true) }
  const seedHeightWeight = (inches: number | null | undefined, ounces: number | null | undefined) => {
    if (!heightTouched.current) { const hf = feetInchesFromInches(inches); setHeightFt(hf.feet); setHeightInPart(hf.inches) }
    if (!weightTouched.current) setWeightSeedOz(ounces && ounces > 0 ? ounces : null)
  }
  const heightParsed = inchesFromFeetInches(heightFt, heightInPart)
  const weightBoth = weightUnit === 'both'
  const weightLinked = linkedWeightBoxes(weightTyped, weightSeedOz)
  const weightBoxUnit: WeightBoxUnit = weightTyped?.unit ?? (weightUnit === 'kg' ? 'kg' : 'lb')
  const weightBox = weightTyped?.text ?? weightBoxFromOunces(weightSeedOz, weightBoxUnit)
  const weightParsed = weightBoth ? weightLinked.parsed : ouncesFromWeightBox(weightBox, weightBoxUnit, weightSeedOz)
  const weightInput = (unit: WeightBoxUnit, value: string) => (
    <label key={unit} style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 11 }}>
      Weight ({unit})
      <input type="number" min={0} step="0.1" value={value} aria-label={unit === 'kg' ? 'Weight kilograms' : 'Weight pounds'}
        placeholder={unit}
        onChange={e => { setWeightTyped({ text: e.target.value, unit }); touchWeight() }} style={{ ...hwInput, width: 80 }} />
    </label>)
  const hwError = (heightDirty && !heightParsed.ok ? heightParsed.error : null)
    ?? (weightDirty && !weightParsed.ok ? weightParsed.error : null)
  /** The measured values for the save payload: only the measurement(s) the operator changed. */
  const hwPayload = () => measurementPayload({ dirty: heightDirty, parsed: heightParsed }, { dirty: weightDirty, parsed: weightParsed })
  // D-CRX-80 (mig 388): ICD-10 typed at enrollment. Pioneer's projected codes win when present.
  const [dxCodes, setDxCodes] = useState('')
  // ASP #4: the Rx number whose PioneerRx codes are in the box, until the operator edits them.
  const [dxFromRx, setDxFromRx] = useState<string | null>(null)
  // Patrick 2026-10-08: codes read from the prescriber's e-script note, not sent coded by PioneerRx.
  const [dxFromNote, setDxFromNote] = useState(false)
  // Codes that CAME from PioneerRx belong to that Rx: they go when the patient changes, the anchor is
  // cleared, or another Rx (without codes) is picked. Codes the operator typed are never touched.
  const dxFromRxRef = useRef<string | null>(null)
  const setPioneerDx = (rxNumber: string | null, codes: string, fromNote = false) => {
    dxFromRxRef.current = rxNumber; setDxFromRx(rxNumber); setDxCodes(codes); setDxFromNote(fromNote)
  }
  const dropPioneerDx = () => { if (dxFromRxRef.current !== null) setPioneerDx(null, '') }
  const typeDx = (codes: string) => { dxFromRxRef.current = null; setDxFromRx(null); setDxFromNote(false); setDxCodes(codes) }
  const [priority, setPriority] = useState(presetPriority ?? 'normal')       // CR-G2 (default Normal; PA-queue prefill)
  // Owner 2026-10-04: every enrollment has a pharmacist — by default the person enrolling (when they are
  // one), or the therapy's current pharmacist in edit mode; changeable before saving.
  const { me, hasPermission } = useAuth()
  const roster = usePharmacistRoster(true)
  const assignRph = useAssignReview()
  const [rphId, setRphId] = useState<string>(presetPharmacistId ?? '')
  const rphTouched = useRef(false)
  useEffect(() => {
    if (rphTouched.current || rphId || !roster.data) return
    setRphId(defaultPharmacist(roster.data.items, me?.account_id))
  }, [roster.data, me?.account_id, rphId])
  const pickRph = (id: string) => { rphTouched.current = true; setRphId(id) }
  const [consultationNeeded, setConsultationNeeded] = useState(true)  // CR-G4
  // D-CRX-86: sent only when the operator picks; create mode clears it when the patient changes.
  const pregnancy = usePregnancyRadio(isEdit ? undefined : patient?.id ?? null)
  const [doseAmt, setDoseAmt] = useState('')   // G-3: dose split from SIG
  const [freq, setFreq] = useState('')          // G-3: frequency split from SIG
  // UAT 2026-08-11 (owner): the outreach date must MATCH the status — reached captures the contact
  // date; unable_to_reach carries the next-attempt date (required); in_progress may carry one.
  const [outreachReachedAt, setOutreachReachedAt] = useState('')
  const [outreachNextAt, setOutreachNextAt] = useState('')
  const [labQ, setLabQ] = useState<DoorLab[]>([])
  const [labAns, setLabAns] = useState<Record<string, { collected: '' | 'yes' | 'no'; date: string; noResults?: boolean; resultsOn?: string }>>({})
  // QA of #2604 — which lab rows the OPERATOR actually answered, as opposed to the ones this panel
  // seeded for display when it opened. On EDIT the two are very different things: the seed writes a
  // definite yes/no for every analyte, and posting those as answers told the server "not collected"
  // about labs nobody had looked at. recompute-labs treats an explicit false as an instruction to
  // clear drawn_at and withdraw the result projection, so the edit payload now carries only what
  // was touched. Create is unaffected — nothing exists yet to contradict.
  const [labTouched, setLabTouched] = useState<Set<string>>(new Set())
  // D-CRX-95 PR-C (ticket #1025). Nothing below renders unless the pharmacy's lab reader is on (the
  // lab-check says so), so a pharmacy without it — BioAccess today — sees exactly the form it had.
  const [labReader, setLabReader] = useState(false)       // source line + "Enter newer result"
  const [labListEdit, setLabListEdit] = useState(false)   // + Add lab / Waive / Restore
  const [labAdded, setLabAdded] = useState<api.LabRequirementItem[]>([])   // create: rides the payload
  const [labWaived, setLabWaived] = useState<WaivedLab[]>([])              // create: local; edit: the server's
  const [labNewer, setLabNewer] = useState<Set<string>>(new Set())         // on-file rows opened for a newer draw
  const [labDlg, setLabDlg] = useState<{ action: LabRequirementAction; analyte?: string } | null>(null)
  const [labDlgBusy, setLabDlgBusy] = useState(false)
  const [labDlgErr, setLabDlgErr] = useState<string | null>(null)
  const [labNote, setLabNote] = useState<string | null>(null)
  const isRph = hasPermission('carerx.pharmacist')
  const qc = useQueryClient()
  const changeLab = useChangeTherapyLab()
  const therapyLabs = (tid: string) => fetchTherapyLabs(qc, tid).then(toDoorLabs)

  // Anchor the enrollment to one prescription — from the picker, or preset by a PA-approved row.
  // UAT 2026-08-11 (owner): Pioneer data FOLLOWS the anchor - the SIG and the dose parsed from it
  // always reflect the picked Rx (they render read-only below; OwlRx never writes back to Pioneer).
  // Frequency only pre-fills when empty - it stays adjustable (a pharmacy scheduling decision).
  const anchorRx = (x: api.EnrollRxRow) => {
    setPresetNote(null)   // a prescription is anchored: the preset warning (L14) no longer applies
    setAnchored(x); setRxNumber(x.rx_number ?? ''); if (x.formulary_id) setFormularyId(x.formulary_id)
    setDoseSig(x.sig ?? '')
    setDoseAmt(x.sig?.match(DOSE_RE)?.[0]?.toUpperCase() ?? '')
    const fr = x.sig?.match(FREQ_RE)?.[0]
    if (fr) setFreq(f => f || fr.toUpperCase())
    // ASP #4: PioneerRx is the source of truth for the diagnosis — when the picked Rx carries codes
    // they fill the box (primary first), and the operator sees where they came from. An Rx without
    // codes leaves whatever is typed alone.
    if (x.diagnosis_codes) setPioneerDx(x.rx_number ?? '', x.diagnosis_codes, x.diagnosis_source === 'escript_note')
    else dropPioneerDx()
  }

  // STALE RESPONSES (review 2026-10-02, flaky I3): a response that lands after the patient changed,
  // or after this effect re-ran (StrictMode runs it twice), must be dropped. Applied late, the old
  // patient's prescriptions — and the preset anchor with its PioneerRx ICD-10 codes — were written
  // over the current form: in the lab, Clear was undone by the second response; in production, a
  // quick patient switch could fill the form with the PREVIOUS patient's Rx and diagnosis codes.
  // Every patient-keyed read below carries the same `live` guard.
  useEffect(() => {
    let live = true
    setRxRows([]); setRxBanner(null); setPresetNote(null); setRxNumber(''); setAnchored(null); setRxState(patient?.id ? 'loading' : 'idle'); setRxStartDate(null)
    dropPioneerDx()
    if (patient?.id) api.listEnrollmentRx(patient.id)
      .then(r => {
        if (!live) return
        setRxRows(r.items); setRxBanner(r.patient); setRxState('ok'); setRxStartDate(r.rx_start_date ?? null)
        if (!isEdit) seedHeightWeight(r.patient?.height_inches, r.patient?.weight_ounces)
        // Started from a PA-approved row: anchor THAT prescription, as if picked from the list.
        // A refill's display number (118705-1) matches its base prescription row (rx_number is never suffixed).
        const presetBase = presetRxNumber?.replace(/-\d+$/, '')
        const preset = !isEdit && presetRxNumber
          ? r.items.find(x => x.rx_number === presetRxNumber) ?? r.items.find(x => x.rx_number === presetBase)
          : undefined
        if (preset && !preset.has_active_therapy) { anchorRx(preset); setRxOpen(true); setPresetNote(null) }
        else if (!isEdit && presetRxNumber)
          setPresetNote(preset
            ? `Rx ${presetRxNumber} already has an open therapy — continue that enrollment instead, or pick another prescription below.`
            : `Rx ${presetRxNumber} from the PA-approved list is not on this patient's prescription list (it may have changed in PioneerRx). Pick the prescription below.`)
      })
      .catch((e: unknown) => {
        if (!live) return
        const m = e instanceof Error ? e.message : String(e)
        // patient_not_found = the id has no row in the prescription store — an empty state, not a failure
        if (/patient_not_found|404/.test(m)) { setRxRows([]); setRxBanner(null); setRxState('ok') }
        else { setRxErr(m); setRxState('error') }
      })
    return () => { live = false }
  }, [patient?.id])

  useEffect(() => {
    // ASP-CRX-13: insurance NAME display-only (spec: never BIN/PCN). Source: the enrollment record if
    // present (Pioneer-fed ins_payer/ins_plan); absent = not shown.
    let live = true
    if (patient?.id) api.getCareRxEnrollment(patient.id)
      .then(r => { if (live) setInsName([r.enrollment?.ins_payer, r.enrollment?.ins_plan].filter(Boolean).join(' — ') || null) })
      .catch(() => { if (live) setInsName(null) })
    else setInsName(null)
    return () => { live = false }
  }, [patient?.id])

  useEffect(() => {
    // UAT 2026-07-29 (Patrick): the dropdown reads DRUG REFERENCE (D-CRX-43/R-19 single surface).
    // Each option's value is the linked formulary id (the therapy anchor); unlinked drugs are
    // listed but blocked (Andrew red-line, WS7). Formulary fallback only if the endpoint is absent.
    api.enrollmentDrugList()
      .then(r => setDrugs((r.items ?? []).map(d => ({ id: d.linked && d.formulary_id ? d.formulary_id : `unlinked:${d.drug_reference_id}`, name: d.linked ? d.name : `${d.name} — not linked (red-line pending)`, care_plan_frequency: d.care_plan_frequency }))))
      .catch(() => api.listCareRxFormulary({ active: true })
        .then(r => setDrugs((r.items ?? []).map((f: { id: string; name: string }) => ({ id: f.id, name: f.name }))))
        .catch(() => setDrugs([])))
  }, [])

  useEffect(() => {
    api.listSiteOfCareTypes(true).then(r => setSocTypes(r.items)).catch(() => setSocTypes([]))
  }, [])
  const visibleRx = showAllRx ? rxRows : rxRows.filter(x => x.pa_approved)
  // UAT 2026-08-11 (owner): Pioneer-sourced values are READ-ONLY — OwlRx never writes back to
  // PioneerRx, so an edit here silently diverges from the source of truth. The SIG (and the dose
  // parsed from it) locks whenever a Pioneer Rx anchors the enrollment; frequency stays adjustable.
  // Extra data can be ADDED on top; the OG Pioneer data cannot change.
  // Owner review 2026-08-13 (B-2): the lock holds in EDIT mode too — the therapy's drug finds its
  // Pioneer Rx by formulary anchor, so reopening an enrollment cannot re-open Pioneer's dose.
  const anchoredRx = isEdit
    ? rxRows.find(x => !!x.sig && !!x.formulary_id && x.formulary_id === (editTherapy?.formulary_id ?? '')) ?? null
    : anchored
  const pioneerSig = anchoredRx?.sig ?? ''
  const pioneerDose = pioneerSig.match(DOSE_RE)?.[0]?.toUpperCase() ?? ''
  // QA of #2604: ASP's Pioneer feed carries 0.00 rather than NULL for a height and weight it does
  // not hold, so a plain null check read a zero as a measurement: the banner printed Height 0'0", the
  // enrollment form printed 0'0", 0 lb, and the D-CRX-80 capture box — gated on null — never
  // appeared, leaving no way to enter the real values. Height and weight drive dosing. A zero is
  // an absent measurement; the server now NULLIFs it too, and this guards the older shape.
  const hasHeight = !!rxBanner?.height_inches
  const hasWeight = !!rxBanner?.weight_ounces
  useEffect(() => { api.listLabProviders().then(d => setLabProviders(d.items)).catch(() => setLabProviders([])) }, [])
  // Edit mode: load the saved Intake-A extras + current site once.
  useEffect(() => {
    if (!isEdit || !patient?.id) return
    let live = true
    if (editTherapy?.site) setSiteOfCare(editTherapy.site)
    // D-CRX-80: seed the diagnosis boxes from what this therapy already carries, so an edit SHOWS
    // the recorded codes instead of two blank inputs the operator has to retype to keep. The draft
    // read is the one place that already exposes them.
    if (editTherapy?.therapy_id)
      api.getCareRxEnrollmentDraft(editTherapy.therapy_id)
        .then(d => {
          if (!live) return
          // QA of #2604: seed the THERAPY-grain controls too. Therapy type, priority and
          // consultation-needed used to open on their create-time defaults ("New Start", "Normal",
          // "Yes") whatever the therapy held, so the panel misreported the record before it
          // discarded the operator's change. Facility likewise: seeding it is what lets the
          // required-facility validation pass without re-picking it on every save.
          const t = d.therapy
          if (t?.therapy_type) setTherapyType(t.therapy_type)
          if (t?.priority) setPriority(t.priority)
          if (typeof t?.consultation_needed === 'boolean') setConsultationNeeded(t.consultation_needed)
          if (t?.facility_id) setFacilityId(t.facility_id)
          pregnancy.seed(t?.pregnancy_screening, t?.breastfeeding_screening)
          // ASP #5: the enrollment's own height/weight (operator measurement first, then Pioneer).
          seedHeightWeight(d.patient?.height_inches, d.patient?.weight_ounces)
          const dx = d.diagnoses ?? []
          if (dx.length === 0) return
          // Primary first, so re-saving preserves which one it is — the field's own convention.
          const ordered = [...dx].sort((a, b) => Number(!!b.is_primary) - Number(!!a.is_primary))
          setDxCodes(ordered.map(x => x.code).filter(Boolean).join(', '))
        })
        .catch(() => { /* a seeding read must never block the dialog */ })
    api.getCareRxEnrollment(patient.id).then(r => {
      if (!live) return
      const e = r.enrollment ?? {}
      const ds = String(e.dose_sig ?? '')
      const m = ds.match(/^DOSE\s+(.+?)\s+\u00b7\s+FREQ\s+(.+?)(?:\s+\u00b7\s+(.*))?$/)
      if (m) { setDoseAmt(m[1]); setFreq(m[2]); if (m[3]) setDoseSig(m[3]) } else if (ds) setDoseSig(ds)
      if (e.outreach_status) setOutreach(String(e.outreach_status))
      if (e.outreach_reached_at) setOutreachReachedAt(String(e.outreach_reached_at).slice(0, 10))
      if (e.outreach_next_at) setOutreachNextAt(String(e.outreach_next_at).slice(0, 10))
      if (e.intake_notes) setNotes(String(e.intake_notes))
      if (e.next_infusion_at) setNextInf(String(e.next_infusion_at).slice(0, 10))
      if (e.deliver_on) setDeliverOn(String(e.deliver_on).slice(0, 10))
      if (e.city) setECity(String(e.city))
      if (e.zip) setEZip(String(e.zip))
      if (e.lab_provider_id) setLabProviderId(String(e.lab_provider_id))
    }).catch(() => { /* extras optional */ })
    return () => { live = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, patient?.id])
  const socType = socTypes.find(t => t.code === siteOfCare) ?? null
  // QA of #2604: this cleared facilityId on EVERY run, including the first — so in edit mode it
  // wiped the value seeded from the therapy microseconds earlier, and the required-facility
  // validation then blocked Save until the operator re-picked a facility the panel already knew.
  // A facility belongs to a site of care, so clear it when the site actually CHANGES, not on mount.
  const prevSite = useRef<string | null>(null)
  useEffect(() => {
    if (prevSite.current !== null && prevSite.current !== siteOfCare) setFacilityId('')
    prevSite.current = siteOfCare
    if (socType?.requires_facility)
      // PICKER for one type during enrollment — paged endpoint, so ask for the full page.
      // A facility missing here means a patient is enrolled against the wrong site of care.
      api.listFacilities(socType.code, true, { page: 1, pageSize: 200 }).then(r => setFacilities(r.items)).catch(() => setFacilities([]))
    else setFacilities([])
  }, [siteOfCare, socType?.requires_facility, socType?.code])
  // B3 (Patrick 2026-07-29): Home uses the patient address — prefill the editable city/zip extras
  // from Pioneer's banner address when empty. Pioneer stays the owner; nothing writes back.
  useEffect(() => {
    if (siteOfCare !== 'home' || !rxBanner) return
    setECity(p => p || (rxBanner.city ?? ''))
    setEZip(p => p || (rxBanner.zip_code ?? ''))
  }, [siteOfCare, rxBanner])

  useEffect(() => {
    let live = true
    setLabQ([]); setLabAns({}); setLabTouched(new Set())
    setLabReader(false); setLabListEdit(false); setLabAdded([]); setLabWaived([]); setLabNewer(new Set()); setLabNote(null)
    if (patient?.id && formularyId)
      api.enrollmentLabCheck(patient.id, formularyId).then(async r0 => {
        if (!live) return
        // D-CRX-95: an existing enrollment answers from its own list (added in, waived out).
        const r = r0.list_edit && editTherapy?.therapy_id
          ? await therapyLabs(editTherapy.therapy_id).catch(() => ({ items: r0.items as DoorLab[], waived: [] as WaivedLab[] }))
          : { items: r0.items as DoorLab[], waived: [] as WaivedLab[] }
        if (!live) return
        setLabReader(r0.reader === 'on'); setLabListEdit(!!r0.list_edit); setLabWaived(r.waived)
        setLabQ(r.items)
        // Seed each answer from the SAVED state so reopening reflects reality instead of a blank
        // control: a lab that's current reads 'Collected' (with its date); anything not current
        // reads 'Not collected' — which is exactly what an un-drawn required lab means. Staff can
        // still change it. (Fixes "radios go blank after save" — now it's a dropdown that sticks.)
        seedLabAns(r.items)
      }).catch(() => { if (live) setLabQ([]) })
    return () => { live = false }
  }, [patient?.id, formularyId])
  function seedLabAns(items: DoorLab[]) {
    setLabAns(prev => {
      const next = { ...prev }
      for (const it of items)
        if (next[it.analyte] === undefined)
          next[it.analyte] = it.current
            ? { collected: 'yes', date: (it.last_date ?? '').slice(0, 10), resultsOn: (it.last_date ?? '').slice(0, 10) }
            : { collected: 'no', date: '' }
      return next
    })
  }

  // ── D-CRX-95: change the lab list. No enrollment yet → kept here and sent with the create (written
  // in the same transaction, or not at all). An existing enrollment → saved now, then re-read.
  const labWaivedSet = new Set(labWaived.map(w => w.analyte))
  const shownLabQ = labQ.filter(q => !labWaivedSet.has(q.analyte))
  const applyLabChange = async (action: LabRequirementAction, item: api.LabRequirementItem) => {
    setLabDlgErr(null)
    const tid = editTherapy?.therapy_id
    if (tid) {
      setLabDlgBusy(true)
      try {
        const res = await changeLab.mutateAsync({ therapyId: tid, body: { action, ...item } })
        const r = await therapyLabs(tid)
        setLabQ(r.items); setLabWaived(r.waived); seedLabAns(r.items); setLabDlg(null)
        setLabNote(res.in_tracker
          ? `Saved. ${res.missing_labs.length} lab(s) not current — this enrollment is in the Lab Tracker.`
          : 'Saved. Every required lab is current.')
      } catch (e) { setLabDlgErr(friendlyError(e, 'The lab list could not be changed.')) }
      finally { setLabDlgBusy(false) }
      return
    }
    if (action === 'add') {
      if (!patient?.id || !formularyId) return
      setLabDlgBusy(true)
      try {
        const r = await api.enrollmentLabCheck(patient.id, formularyId, [item.analyte])
        const found: DoorLab = { ...(r.items[0] ?? { analyte: item.analyte, last_date: null, current: false }), added: true }
        setLabQ(q => [...q, found]); seedLabAns([found]); setLabAdded(a => [...a, item]); setLabDlg(null)
      } catch (e) { setLabDlgErr(friendlyError(e, 'The lab could not be checked.')) }
      finally { setLabDlgBusy(false) }
    } else if (action === 'waive') {
      setLabWaived(w => [...w, { ...item, label: analyteLabel(item.analyte) }]); setLabDlg(null)
    }
  }
  /** Create mode only: undo a local change before anything is written. */
  const undoLocalLab = (analyte: string) => {
    if (labAdded.some(a => a.analyte === analyte)) {
      setLabAdded(a => a.filter(x => x.analyte !== analyte)); setLabQ(q => q.filter(x => x.analyte !== analyte))
    } else setLabWaived(w => w.filter(x => x.analyte !== analyte))
  }
  const reopenOnFile = (analyte: string, open: boolean) => {
    setLabNewer(s => { const n = new Set(s); if (open) n.add(analyte); else n.delete(analyte); return n })
    if (!open) {
      // Closing again puts the on-file answer back: nothing the operator typed for it is sent.
      const it = labQ.find(q => q.analyte === analyte)
      setLabTouched(s => { const n = new Set(s); n.delete(analyte); return n })
      setLabAns(m => { const n = { ...m }; delete n[analyte]; return n })
      if (it) seedLabAns([it])
    }
  }
  const labVerdict = (q: api.LabCheckItem) => {
    const a = labAns[q.analyte]
    if (q.current) return 'current'
    if (a?.collected === 'yes' && a.noResults) return 'missing'  // G-10: drawn, no results back
    if (a?.collected === 'yes' && a.date) {
      const d = new Date((a.resultsOn || a.date) + 'T00:00:00')
      return d >= new Date(Date.now() - 365 * 864e5) ? 'current' : 'stale'
    }
    return 'missing'
  }
  const labMissing = shownLabQ.filter(q => labVerdict(q) !== 'current')

  const submit = async (withOverride: boolean) => {
    if (!patient || (!isEdit && !formularyId)) { setErr('Pick a patient and a drug.'); return }
    if (socTypes.length > 0) {
      if (!siteOfCare) { setErr('Site of care is required.'); return }
      if (socType?.requires_facility && !facilityId) { setErr(`${socType.label} requires a facility from the directory.`); return }
    }
    if (outreach === 'unable_to_reach' && !outreachNextAt) { setErr('"Unable to reach" needs the reach-again date — when will the patient be contacted next?'); return }
    if (hwError) { setErr(hwError); return }
    setBusy(true); setErr('')
    try {
      if (isEdit && editTherapy) {
        const extras: Record<string, string> = {}
        { const composed = [doseAmt.trim() && `DOSE ${doseAmt.trim()}`, freq.trim() && `FREQ ${freq.trim()}`, doseSig.trim()].filter(Boolean).join(' \u00b7 ')
          if (composed) extras.dose_sig = composed }
        if (outreach) extras.outreach_status = outreach
        if (outreach === 'reached') extras.outreach_reached_at = outreachReachedAt || new Date().toISOString().slice(0, 10)
        if (outreachNextAt && (outreach === 'unable_to_reach' || outreach === 'in_progress')) extras.outreach_next_at = outreachNextAt
        if (notes.trim()) extras.intake_notes = notes.trim()
        if (nextInf) extras.next_infusion_at = nextInf
        if (deliverOn) extras.deliver_on = deliverOn
        if (eCity.trim()) extras.city = eCity.trim()
        if (eZip.trim()) extras.zip = eZip.trim()
        if (labProviderId) extras.lab_provider_id = labProviderId
        if (Object.keys(extras).length > 0) await api.putCareRxEnrollment(patient.id, extras)
        // QA of #2604: the THERAPY-grain half. These controls rendered on the edit panel and were
        // dropped on Save — the ICD-10 box was even seeded from the therapy and then discarded, so
        // correcting a diagnosis code and reopening showed the old one back. Advisory like the
        // admin-config write below: a tenant missing one of these columns skips that field rather
        // than failing the whole edit.
        try {
          const ir = await api.saveCareRxEnrollmentIntake(editTherapy.therapy_id, {
            therapy_type: therapyType || undefined,
            priority: priority || undefined,
            consultation_needed: consultationNeeded,
            facility_id: facilityId || undefined,
            diagnosis_codes: dxCodes.trim() || undefined,
            primary_diagnosis_code: dxCodes.split(',')[0]?.trim() || undefined,
            ...hwPayload(),
            pregnancy_status: pregnancy.payload(),
          })
          const pn = pregnancyNote(ir.pregnancy_screening)
          if (pn) setExtrasNote(prev => prev || pn)
        } catch { setExtrasNote(prev => prev || 'Saved, but the therapy details (type, priority, diagnosis) could not be updated.') }
        // The pharmacist, when changed: named on the therapy without starting the review.
        if (rphId && rphId !== (presetPharmacistId ?? '')) {
          try { await assignRph.mutateAsync({ therapyId: editTherapy.therapy_id, rphId, keepStatus: true }) }
          catch { setExtrasNote(prev => prev || 'Saved, but the pharmacist could not be changed.') }
        }
        if (siteOfCare && siteOfCare !== (editTherapy.site ?? ''))
          await api.switchCareRxPathway(editTherapy.therapy_id, { mode: siteOfCare, reason: 'Edited at Intake A (enrollment edit)' })
        // D-CRX-72 — the administration adjustments for THIS patient. Sent whole (blank = cleared),
        // and advisory: an environment without mig 372 answers 503 and the rest of the edit stands.
        try {
          await api.saveEnrollmentAdminConfig(editTherapy.therapy_id, {
            premed_override: adminOv.premed_override?.trim() || null,
            kit_override: adminOv.kit_override?.trim() || null,
            flush_override: adminOv.flush_override?.trim() || null,
            rate_rule_override: adminOv.rate_rule_override?.trim()
              ? JSON.stringify({ note: adminOv.rate_rule_override.trim() }) : null,
            protocol_override: adminOv.protocol_override,
          })
        } catch (e) {
          // Advisory as before — except a refused protocol choice for this patient (D-CRX-89), which must
          // not disappear silently: the nursing documents would print the drug's protocol instead.
          if (e instanceof api.ApiError && e.body?.field === 'protocol_override')
            setExtrasNote(prev => prev || `Saved, but the protocol choice for this patient was not: ${friendlyError(e)}`)
        }
        // D-CRX-44 fix: an edit can re-open a lab violation (e.g. marking labs not collected) —
        // recompute server-side so the enrollment routes back INTO the Lab Tracker (or exits it if
        // now satisfied). The create path stamps the tracker inline; edit needs this explicit call.
        try {
          // Only the analytes the operator actually answered. An untouched row carries the panel's
          // opening seed, not a statement about the patient, and the server reads a `collected:
          // false` as licence to erase the draw dates and the result projection.
          const labsPayload = labQ.filter(q => labTouched.has(q.analyte))
            .map(q => ({ analyte: q.analyte, collected: labAns[q.analyte]?.collected === 'yes',
              collected_on: labAns[q.analyte]?.date || undefined, no_results: labAns[q.analyte]?.noResults || undefined,
              results_on: labAns[q.analyte]?.resultsOn || undefined }))
          const rec = await api.recomputeLabViolation(editTherapy.therapy_id, labsPayload)
          if (rec.in_tracker) setExtrasNote('Saved — this enrollment has a lab violation and is now in the Lab Tracker.')
        } catch { /* advisory: never block the save on the recompute */ }
        onCreated(); return
      }
      const r = await api.createCareRxEnrollment({
        patient_id: patient.id, formulary_id: formularyId,
        twin_verified: twinVerified || undefined,
        override: withOverride || undefined,
        override_reason: withOverride ? overrideReason : undefined,
        site_of_care: siteOfCare || undefined,
        facility_id: facilityId || undefined,
        rx_number: rxNumber || undefined,
        therapy_type: therapyType || undefined,
        priority: priority || undefined,
        consultation_needed: consultationNeeded,
        assigned_rph_id: rphId || undefined,
        premed_override: adminOv.premed_override?.trim() || undefined,
        kit_override: adminOv.kit_override?.trim() || undefined,
        flush_override: adminOv.flush_override?.trim() || undefined,
        rate_rule_override: adminOv.rate_rule_override?.trim()
          ? JSON.stringify({ note: adminOv.rate_rule_override.trim() }) : undefined,
        protocol_override: adminOv.protocol_override,
        labs: shownLabQ.map(q => ({ analyte: q.analyte, collected: labAns[q.analyte]?.collected === 'yes', collected_on: labAns[q.analyte]?.date || undefined, no_results: labAns[q.analyte]?.noResults || undefined, results_on: labAns[q.analyte]?.resultsOn || undefined })),
        lab_requirements: labListEdit && (labAdded.length > 0 || labWaived.length > 0)
          ? { added: labAdded.length ? labAdded : undefined, waived: labWaived.length ? labWaived.map(({ analyte, reason_code, reason_text }) => ({ analyte, reason_code, reason_text })) : undefined }
          : undefined,
        // D-CRX-80: stored in the units the enrollment snapshot uses (inches, ounces), converted
        // from the units a pharmacy actually measures in. Omitted unless the operator changed a box
        // (ASP #5) so an untouched prefill never overwrites a value PioneerRx later supplies.
        ...hwPayload(),
        // One field: the FIRST code typed is the primary. That is how a prescriber writes them,
        // and it spares the operator restating by name something they already said by ordering.
        diagnosis_codes: dxCodes.trim() || undefined,
        primary_diagnosis_code: dxCodes.split(',')[0]?.trim() || undefined,
        pregnancy_status: pregnancy.payload(),
      })
      { const pn = pregnancyNote(r.pregnancy_screening); if (pn) setExtrasNote(prev => prev || pn) }
      // ASP-CRX-13 (spec §A Stage 1): persist the Intake-A extras on the enrollment right after the
      // door succeeds. Only non-empty fields are sent; a tenant awaiting mig 257 answers with a named
      // 422 which we surface as a soft note — the enrollment itself is already created.
      const extras: Record<string, string> = {}
      { const composed = [doseAmt.trim() && `DOSE ${doseAmt.trim()}`, freq.trim() && `FREQ ${freq.trim()}`, doseSig.trim()].filter(Boolean).join(' \u00b7 ')
        if (composed) extras.dose_sig = composed }
      if (paFu) extras.pa_followup_at = paFu
      if (outreach) extras.outreach_status = outreach
      if (outreach === 'reached') extras.outreach_reached_at = outreachReachedAt || new Date().toISOString().slice(0, 10)
      if (outreachNextAt && (outreach === 'unable_to_reach' || outreach === 'in_progress')) extras.outreach_next_at = outreachNextAt
      if (notes.trim()) extras.intake_notes = notes.trim()
      if (nextInf) extras.next_infusion_at = nextInf
      if (deliverOn) extras.deliver_on = deliverOn
      if (eCity.trim()) extras.city = eCity.trim()
      if (eZip.trim()) extras.zip = eZip.trim()
      if (labProviderId) extras.lab_provider_id = labProviderId
      if (Object.keys(extras).length > 0) {
        try { await api.putCareRxEnrollment(patient.id, extras) }
        catch (xe: any) {
          const xb = xe?.body
          setExtrasNote(xb?.error === 'pending_migration'
            ? 'Enrollment created. The Dose/SIG, PA follow-up, and outreach fields will save once migration 257 is applied to this pharmacy.'
            : 'Enrollment created; the Intake-A extras could not be saved — edit them from the enrollment review.')
        }
      }
      // R-18: home meds no longer captured at enrollment (Pioneer allergies replace them here).
      // Site of care at create (spec Stage 1): the door returned the therapy — bind the pathway now.
      try {
        // D-CRX-42: the door create persists site + facility itself; the pathway-switch fallback
        // remains only for tenants without mig 295 (socTypes empty ⇒ server ignored site_of_care).
        if (siteOfCare && r.therapy_id && socTypes.length === 0)
          await api.switchCareRxPathway(r.therapy_id, { mode: siteOfCare, reason: 'Set at enrollment (Stage 1 intake)' })
      } catch { setExtrasNote(prev => prev || 'Enrollment created; set the site of care from the therapy lane.') }
      setDone({ pcw_id: r.pcw_id, therapy_archetype: r.therapy_archetype, pcw_existing: r.pcw_existing, routing: r.routing, missing_labs: r.missing_labs })
      onCreated({ pcw_id: r.pcw_id })
    } catch (e) {
      const body = (e as { body?: { error?: string; twins?: typeof twins; existing?: typeof dups; message?: string } }).body
      if (body?.error === 'twin_collision') { setTwins(body.twins ?? []); setErr('Possible duplicate patient — confirm the chart below (§3.1).') }
      else if (body?.error === 'duplicate_open_therapy') { setDups(body.existing ?? []); setErr('An open therapy already exists for this patient + drug.') }
      // D-CRX-87b: the pharmacy combines same-drug Rx and this patient has an open enrollment for the drug —
      // attach the Rx (above), or give a reason to start a new enrollment (the same audited override).
      else if (body?.error === 'attach_available') { setDups(body.existing ?? []); setErr(body.message ?? 'Attach this prescription to the open enrollment, or give a reason to start a new one.') }
      else setErr(body?.message ?? (e as Error).message ?? 'Failed')
    } finally { setBusy(false) }
  }

  return (
    // D-CRX-80: inside a SidePanel the host already draws the frame and names the record, so the
    // door drops its own bordered card and title rather than nesting a second heading and a second
    // border inside the first. Standalone callers (the patient page) keep the card.
    <div style={embedded
      ? { display: 'flex', flexDirection: 'column', gap: 10 }
      : { ...card, border: '1px solid var(--color-primary)', display: 'flex', flexDirection: 'column', gap: 10 }}>
      {!embedded && <div style={{ fontWeight: 700, fontSize: 14 }}>New enrollment</div>}
      {done ? (
        <div style={{ fontSize: 13 }}>
          ✅ Enrollment ready — therapy archetype <b>{done.therapy_archetype}</b>{extrasNote ? <div style={{ marginTop: 6, color: '#b45309', fontSize: 12.5 }}>{extrasNote}</div> : null}{done.pcw_existing ? ' (existing open PCW resumed)' : ' (PCW created in the intake queue)'}. It is now in the queue below.
          {done.routing === 'lab_tracking' && (
            <div style={{ marginTop: 8, padding: '8px 11px', borderRadius: 8, background: '#fffbeb', border: '0.5px solid #d97706', color: '#7c4a03', fontSize: 12.5 }}>
              ⚠ <b>{done.missing_labs?.length ?? 0} baseline lab(s) required</b>{done.missing_labs?.length ? ` (${done.missing_labs.join(', ')})` : ''} — saved and routed to <b>Lab Tracking</b>. The draw lane has a required row for each.
            </div>)}
          {done.routing === 'clinical_review' && (
            <div style={{ marginTop: 8, padding: '8px 11px', borderRadius: 8, background: '#ecfdf5', border: '0.5px solid #34d399', color: '#065f46', fontSize: 12.5 }}>
              ✓ Labs current or none required — routed to <b>Clinical Review</b>.
            </div>)}
          {/* Owner tester pass 2026-08-14: a transient failure used to skip the lab gate SILENTLY. */}
          {done.routing === 'lab_check_failed' && (
            <div style={{ marginTop: 8, padding: '8px 11px', borderRadius: 8, background: '#fef2f2', border: '0.5px solid #dc2626', color: '#7f1d1d', fontSize: 12.5 }}>
              ⚠ <b>The lab check could not run</b> (transient error). The enrollment saved, but required labs were <b>not</b> wired —
              open <b>Edit enrollment</b> and Save to re-run it before this patient is reviewed.
            </div>)}
          <div style={{ marginTop: 8 }}><button style={linkBtn} onClick={onClose}>Close</button></div>
        </div>
      ) : attachedTo ? (
        <div role="status" style={{ fontSize: 13 }}>
          ✅ Rx <b>{attachedTo.rx}</b> attached to enrollment <b>{attachedTo.ref}</b>. The enrollment now lists it.
          <div style={{ marginTop: 8 }}><button style={linkBtn} onClick={onClose}>Close</button></div>
        </div>
      ) : (<>
        {presetPatient
          ? <div style={{ fontSize: 13 }}>Patient: <b>{presetPatient.label}</b>{presetLanguage !== undefined ? <LanguageBadge language={presetLanguage} /> : <PatientLanguageBadge patientId={presetPatient.id} />}{presetPatient.sub ? ` — ${presetPatient.sub}` : ''}</div>
          : <EntityPicker kind="patient" value={patient} onPick={p => { setPatient(p); setTwins([]); setTwinVerified(false); setDups([]) }} />}
        {patient && !isEdit && (
        <div style={{ margin: '14px 0 6px', paddingTop: 10, borderTop: '0.5px solid var(--color-border, #e5e7eb)' }}>
          <button type="button" onClick={() => setRxOpen(o => !o)}
            style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%', background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left' }}>
            {rxOpen ? <ChevronDown size={14} color="var(--color-text-mute, #9ca3af)" /> : <ChevronRight size={14} color="var(--color-text-mute, #9ca3af)" />}
            <span style={{ fontSize: 13, fontWeight: 700 }}>Prescription &mdash; from PioneerRx</span>
            {/* Collapsed, the header still answers the only question it needs to: what is anchored. */}
            {anchored
              ? <span style={{ fontSize: 11.5, color: 'var(--color-text-soft, #6b7280)' }}>
                  {anchored.rx_number ?? '\u2014'} &middot; {anchored.drug_name ?? '\u2014'}</span>
              : <span style={{ fontSize: 11.5, color: 'var(--color-text-mute, #9ca3af)' }}>
                  {rxState === 'ok' ? `${visibleRx.length} ${showAllRx ? 'on file' : 'PA-approved'} \u00b7 none picked` : 'no prescription anchored'}</span>}
            <span style={{ marginLeft: 'auto', fontSize: 11.5, fontWeight: 600, color: 'var(--color-primary)' }}>{rxOpen ? 'Hide' : 'Pick an Rx'}</span>
          </button>
          {rxOpen && <div style={{ fontSize: 11.5, color: 'var(--color-text-soft, #6b7280)', marginTop: 3 }}>Stage 0 &middot; pick the Rx that anchors this enrollment &middot; PA-approved Rx are listed by default &middot; DAW derives from Pioneer&#39;s DAW code</div>}
        </div>)}
        {rxState === 'ok' && rxBanner && (
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 12, padding: '7px 10px', borderRadius: 8, background: 'var(--color-surface-2, #f9fafb)', border: '0.5px solid var(--color-border, #e5e7eb)' }}>
            {hasRealAllergies(rxBanner.allergies) && <span style={{ color: '#b91c1c', fontWeight: 600 }}>Allergies: {rxBanner.allergies}{rxBanner.allergy_alert_type ? ` (${rxBanner.allergy_alert_type})` : ''}</span>}
            {!hasRealAllergies(rxBanner.allergies) && <span style={{ color: 'var(--color-text-soft, #6b7280)' }}>Allergies: {rxBanner.allergies || 'none on file (Pioneer)'}</span>}
            <span>Height: {hasHeight ? <>{Math.floor((rxBanner.height_inches ?? 0) / 12)}&#39;{Math.round((rxBanner.height_inches ?? 0) % 12)}&quot;</> : 'not on file'}</span>
            <span>Weight: {hasWeight ? formatWeightBanner(rxBanner.weight_ounces ?? 0, weightUnit) : 'not on file'}</span>
            <span style={{ fontSize: 10.5, color: 'var(--color-text-mute, #9ca3af)' }}>PioneerRx — read-only; height/weight snapshot at create</span>
          </div>)}
        {/* D-CRX-80 / ASP #5 — height and weight are ALWAYS editable here. They drive dosing and print
            on the enrollment form, and ASP's PioneerRx sends none (read-only diag #2752: 0 or null for
            every CareRx patient), so capture — not conversion — is the need. What is typed writes the
            enrollment's own snapshot and wins over the Pioneer copy; dbo.patients is never touched
            (PioneerRx stays the source of truth, CLAUDE.md §0.5). Every change is audited. */}
        {(isEdit || (rxState === 'ok' && rxBanner)) && (
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', fontSize: 12,
                        padding: '8px 10px', borderRadius: 8, background: 'var(--color-surface-2, #f9fafb)', border: '0.5px solid var(--color-border, #e5e7eb)' }}>
            <span style={{ fontWeight: 600 }}>Height / weight</span>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 11 }}>
              Height
              <span style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                <input type="number" min={0} step="1" value={heightFt} aria-label="Height feet" placeholder="ft"
                  onChange={e => { setHeightFt(e.target.value); touchHeight() }} style={hwInput} /> ft
                <input type="number" min={0} max={11.5} step="0.5" value={heightInPart} aria-label="Height inches" placeholder="in"
                  onChange={e => { setHeightInPart(e.target.value); touchHeight() }} style={hwInput} /> in
              </span>
            </label>
            {weightBoth
              ? <>{weightInput('kg', weightLinked.kg)}{weightInput('lb', weightLinked.lb)}</>
              : weightInput(weightBoxUnit, weightBox)}
            {hwError && <span role="alert" style={{ color: '#b91c1c', fontSize: 11 }}>{hwError}</span>}
            <span style={{ fontSize: 10.5, color: 'var(--color-text-mute, #9ca3af)' }}>
              {hasHeight || hasWeight ? 'Prefilled from PioneerRx · ' : 'Not on PioneerRx · '}
              recorded on this enrollment, not on the patient record.
            </span>
          </div>)}
        {(isEdit || patient) && <PregnancyStatusField value={pregnancy.value} onChange={pregnancy.pick} />}
        {!isEdit && rxOpen && rxState === 'ok' && rxRows.length === 0 && (
          <div style={{ fontSize: 12, color: 'var(--color-text-soft, #6b7280)', padding: '7px 10px', borderRadius: 8, border: '0.5px dashed var(--color-border, #e5e7eb)' }}>
            No PioneerRx prescriptions on file for this patient — the Rx picker appears when prescriptions exist. Pick the drug from Drug Reference below instead.
          </div>)}
        {!isEdit && rxOpen && rxState === 'ok' && rxRows.length > 0 && (
          <div style={{ display: 'flex', gap: 16, alignItems: 'center', fontSize: 12 }}>
            <label style={{ display: 'flex', gap: 5, alignItems: 'center', cursor: 'pointer' }}>
              <input type="radio" checked={!showAllRx} onChange={() => setShowAllRx(false)} /> PA-approved only
            </label>
            <label style={{ display: 'flex', gap: 5, alignItems: 'center', cursor: 'pointer' }}>
              <input type="radio" checked={showAllRx} onChange={() => setShowAllRx(true)} /> Show all Rx ({rxRows.length})
            </label>
          </div>)}
        {/* Wave-1 #26 (pharmacy ask): a PA-pending Rx is a normal anchor, so it no longer warns —
            the banner keys on drug_matched alone, never on `enrollable` (= drug_matched && PA approved).
            The server still records the PA state on the therapy's anchor_warning at create. */}
        {!isEdit && anchored && !anchored.drug_matched && (
            <div data-testid="anchor-warning" style={{ fontSize: 12, color: '#7f1d1d', background: '#fef2f2', border: '0.5px solid #dc2626', borderRadius: 8, padding: '7px 10px' }}>
              &#9888; <b>Warning &mdash; the drug is not matched</b> in Drug Reference &mdash; pick the drug from the list below to anchor the therapy. The enrollment can proceed; this stays on record.
            </div>)}
        {!isEdit && rxOpen && rxState === 'ok' && rxRows.length > 0 && visibleRx.length === 0 && (
          <div style={{ fontSize: 12, color: 'var(--color-warning-fg, #92400e)', padding: '7px 10px', borderRadius: 8, border: '0.5px dashed var(--color-warning-fg, #d97706)' }}>
            This patient has {rxRows.length} Rx, but none is PA-approved &mdash; switch to <b>Show all Rx</b> to see them. Non-approved Rx can still anchor an enrollment.
          </div>)}
        {presetNote && (
          <div role="status" style={{ fontSize: 12, color: 'var(--color-warning-fg, #92400e)', padding: '7px 10px', borderRadius: 8, border: '0.5px dashed var(--color-warning-fg, #d97706)' }}>
            {presetNote}
          </div>)}
        {!isEdit && rxState === 'error' && (
          <div style={{ fontSize: 12, color: 'var(--color-warning-fg, #92400e)', padding: '7px 10px', borderRadius: 8, border: '0.5px dashed var(--color-warning-fg, #d97706)' }}>
            Couldn&#39;t load the prescription list{rxErr ? <> &mdash; <b style={{ fontFamily: 'monospace', fontWeight: 600 }}>{rxErr}</b></> : ''}. The drug can still be picked from the formulary below.
          </div>)}
        {!isEdit && rxOpen && visibleRx.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            <SearchableSelect
              value={anchored ? String(visibleRx.indexOf(anchored)) : ''}
              ariaLabel="Prescription from PioneerRx"
              placeholder={`Search ${visibleRx.length} prescription${visibleRx.length === 1 ? '' : 's'} \u2014 Rx #, drug or NDC\u2026`}
              options={visibleRx.map((x, ix) => ({
                value: String(ix),
                label: `${x.rx_number ?? '\u2014'} \u00b7 ${x.drug_name ?? '\u2014'}`,
                // Everything the table's columns carried, on one line the typeahead also searches.
                hint: [x.ndc, x.specialty_tag ?? 'not matched', x.claim_status ?? x.rx_status,
                       x.substitution_allowed == null ? null : x.substitution_allowed ? 'substitution permitted' : 'dispense as written',
                       x.has_active_therapy ? 'ACTIVE THERAPY \u2014 cannot anchor' : null,
                       x.before_start_date ? 'before start date' : null,
                       !x.has_active_therapy && x.attach ? `same drug as ${x.attach.therapy_ref} \u2014 attach` : null].filter(Boolean).join(' \u00b7 '),
                // CR-G1: a drug with an active therapy cannot re-anchor (drug change = new therapy).
                disabled: !!x.has_active_therapy,
                // D-CRX-87 §2: before the pharmacy's Rx start date — greyed, still selectable.
                muted: !!x.before_start_date,
              }))}
              onChange={(v: string) => {
                const x = visibleRx[Number(v)]
                if (!x || x.has_active_therapy) return
                anchorRx(x)
              }} />
            {visibleRx.some(x => x.before_start_date) && (
              <div data-testid="rx-start-note" style={{ fontSize: 11, color: 'var(--color-text-soft, #6b7280)' }}>{pickerStartNote(rxStartDate)}</div>)}
            <div style={{ fontSize: 10.5, color: 'var(--color-text-mute, #9ca3af)' }}>
              Picking a prescription anchors the enrollment to it and pre-fills the drug and SIG. DAW derives from Pioneer&#39;s DawCodeID (0 = substitution permitted, NCPDP); Pioneer&#39;s wording shows verbatim when the code is absent.</div>
          </div>)}
        {!isEdit && anchored && (
          // What the table's columns said, for the ONE Rx that matters now.
          <div style={{ border: '0.5px solid var(--color-border, #e5e7eb)', borderRadius: 10, padding: '9px 11px', display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center', fontSize: 12 }}>
            <span style={{ fontFamily: 'monospace', fontWeight: 700 }}>{anchored.rx_number ?? '\u2014'}</span>
            <span style={{ fontWeight: 600 }}>{anchored.drug_name ?? '\u2014'}</span>
            <span style={{ fontFamily: 'monospace', color: 'var(--color-text-soft, #6b7280)' }}>{anchored.ndc ?? '\u2014'}</span>
            {anchored.specialty_tag
              ? <span style={{ fontSize: 10.5, padding: '2px 8px', borderRadius: 20, background: '#EEEDFE', color: '#3C3489' }}>{anchored.specialty_tag}</span>
              : <span style={{ fontSize: 10.5, color: 'var(--color-text-mute, #9ca3af)' }}>not matched</span>}
            <span style={{ color: 'var(--color-text-soft, #6b7280)' }}>{anchored.claim_status ?? anchored.rx_status ?? '\u2014'}</span>
            {anchored.substitution_allowed != null && (
              <span title={anchored.daw_text ?? undefined} style={{ fontSize: 10.5, padding: '2px 8px', borderRadius: 20,
                background: anchored.substitution_allowed ? '#E1F5EE' : '#fef3c7',
                color: anchored.substitution_allowed ? '#085041' : '#92400e' }}>
                DAW {anchored.daw_code} &mdash; {anchored.substitution_allowed ? 'substitution permitted' : 'dispense as written'}</span>)}
            <button type="button" onClick={() => { setAnchored(null); setRxNumber(''); setDoseSig(''); setDoseAmt(''); dropPioneerDx() }}
              style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', fontSize: 11.5, fontWeight: 600, color: 'var(--color-primary)' }}>Clear</button>
          </div>)}
        {!isEdit && anchored?.attach && !anchored.has_active_therapy && anchored.rx_number && (
          // D-CRX-87b: this pharmacy combines same-drug Rx — a new Rx of a drug the patient already has an open
          // enrollment for joins it. Starting a new enrollment instead takes an audited reason (Create below).
          <div role="status" style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', fontSize: 12.5,
                                      padding: '8px 11px', borderRadius: 8, border: '1px solid var(--color-primary)', background: 'var(--color-surface-2, #f9fafb)' }}>
            <span style={{ flex: 1, minWidth: 240 }}>
              Rx <b>{anchored.rx_number}</b> is the same drug as the patient&#39;s open enrollment <b>{anchored.attach.therapy_ref}</b>.
              Attach it there instead of starting a new enrollment.
            </span>
            <AttachRxButton target={anchored.attach} rxNumber={anchored.rx_number} drugName={anchored.drug_name} source="door"
              onDone={() => { if (anchored.attach && anchored.rx_number) setAttachedTo({ ref: anchored.attach.therapy_ref, rx: anchored.rx_number }); onCreated() }}
              // A refused attach was a stale offer: drop it here (the operator can still start an enrollment).
              onRefused={() => setAnchored(a => (a ? { ...a, attach: null } : a))} />
          </div>)}
        <div style={{ margin: '14px 0 6px', paddingTop: 10, borderTop: '0.5px solid var(--color-border, #e5e7eb)' }}>
          <div style={{ fontSize: 13, fontWeight: 700 }}>Enrollment details</div>
          <div style={{ fontSize: 11.5, color: 'var(--color-text-soft, #6b7280)' }}>Stage 1 · Intake A — dose, dates, and scheduling</div>
        </div>
        {/* D-CRX-80 — ICD-10, AFTER the prescription section (owner 2026-09-07).
            It reads off the Rx the operator just anchored, so it belongs below that choice rather
            than above it. ONE field: the first code typed is the primary, which is what §03 needs
            and how a prescriber writes them — a second "primary" box asked the operator to restate
            something they had already said by ordering. Gated on a patient, never on rxState: this
            is typed, not fetched, and the earlier version vanished whenever the Pioneer Rx request
            was in flight, errored, or the patient had no prescriptions at all. */}
        {patient && (
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', fontSize: 12,
                        padding: '8px 10px', borderRadius: 8, border: '0.5px solid var(--color-border, #e5e7eb)' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 11, color: 'var(--color-text-soft, #6b7280)' }}>
              ICD-10 diagnosis code(s) — first is primary
              <input value={dxCodes} placeholder="e.g. L40.0, M07.3"
                onChange={e => typeDx(e.target.value)}
                style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid var(--color-border)', fontSize: 12.5, width: 320 }} />
            </label>
            {dxFromRx !== null && dxFromNote
              ? <span role="status" title="PioneerRx sent no coded diagnosis; these were read from the prescriber's e-script note"
                  style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-warning-fg, #92400e)', background: 'var(--color-warning-bg, #fef3c7)', padding: '1px 6px', borderRadius: 6 }}>
                  From the e-script note (Rx {dxFromRx || '—'}) — verify
                </span>
              : dxFromRx !== null
              ? <span role="status" style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-primary)' }}>From PioneerRx (Rx {dxFromRx || '—'})</span>
              : <span style={{ fontSize: 10.5, color: 'var(--color-text-mute, #9ca3af)' }}>
                  Prints in §03 of the enrollment form. PioneerRx codes take precedence when it sends them.
                </span>}
          </div>)}
        {/* QA of #2604: this label used to OPEN above the ICD-10 block and not close until after
            the Allergies label below, nesting three labels and two controls inside one. Nested
            labels are invalid HTML and leave the association ambiguous for assistive tech. It now
            wraps its own control and nothing else. */}
        <label style={{ fontSize: 11.5, color: 'var(--color-text-mute)', display: 'flex', flexDirection: 'column', gap: 2 }}>
          Drug (Drug Reference){isEdit && <span style={{ marginLeft: 6, fontSize: 10.5 }}>— fixed; a drug change is a NEW therapy (D-CRX-1)</span>}
        {isEdit ? <div style={{ padding: '7px 9px', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 12.5, background: 'var(--color-surface-2, #f9fafb)' }}>{editTherapy?.drug_name ?? drugs.find(d => d.id === formularyId)?.name ?? '—'}</div>
        : <SearchableSelect value={formularyId}
          onChange={(v: string) => {
            // Unlinked Drug Reference rows cannot anchor a therapy (blocked fail-safe, WS7).
            if (v.startsWith('unlinked:')) { setErr('That drug has no CareRx formulary link yet — it needs the red-line load before it can anchor an enrollment.'); return }
            setErr(''); setFormularyId(v); setDups([])
          }}
          options={drugs.map(d => ({ value: d.id, label: d.name }))}
          placeholder="Select drug (Drug Reference)…" />}
        </label>
        {(() => { const d = drugs.find(x => x.id === formularyId)
          return d?.care_plan_frequency
            ? <div style={{ fontSize: 11.5, color: 'var(--color-text-mute)', marginTop: 3 }}>Care plan frequency: <b>{d.care_plan_frequency}</b> <span style={{ opacity: .7 }}>(Drug Reference)</span></div>
            : null })()}
        {/* Supply readiness (round table 2026-08-31): drug-PROFILE facts from the supply generator,
            read-only decoration under the anchor drug. Fail-open — any load error renders nothing
            and enrollment proceeds untouched. These are Drug Reference facts, never patient orders
            (Andrew's wording condition: attribution stays on the strip). */}
        <SupplyReadinessStrip formularyId={formularyId} />

        {/* UAT 2026-07-30 #3: allergies as a proper FIELD (per-Rx Pioneer capture, read-only). */}
        <label style={{ fontSize: 11.5, color: 'var(--color-text-mute)', display: 'flex', flexDirection: 'column', gap: 2, marginTop: 8 }}>
          Allergies (PioneerRx — read-only)
          <input readOnly value={rxBanner?.allergies ?? 'None on file'} tabIndex={-1}
            style={{ padding: '7px 9px', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 12.5,
                     background: 'var(--color-surface-2, #f9fafb)', color: hasRealAllergies(rxBanner?.allergies) ? '#b91c1c' : 'var(--color-text-mute)',
                     fontWeight: hasRealAllergies(rxBanner?.allergies) ? 600 : 400 }} />
        </label>
        {/* ASP-CRX-13 / spec §A Stage 1 — Intake-A fields. PA status itself stays read-only from the
            rx pipeline; insurance shows NAME only elsewhere (never BIN/PCN); city/zip are Pioneer-owned
            display on the queue rows. */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <label style={{ fontSize: 11.5, color: 'var(--color-text-mute)', display: 'flex', flexDirection: 'column', gap: 2 }}>
            Therapy type
          <SearchableSelect value={therapyType} onChange={(v: string) => setTherapyType(v)}
            options={['New Start', 'Refill', 'Continuation', 'Restart', 'New Cycle'].map(x => ({ value: x, label: x }))} />
          </label>
          <label style={{ fontSize: 11.5, color: 'var(--color-text-mute)', display: 'flex', flexDirection: 'column', gap: 2 }}>
            Dose / SIG{pioneerSig ? ' (PioneerRx — read-only)' : ''}
            <input value={doseSig} readOnly={!!pioneerSig} tabIndex={pioneerSig ? -1 : undefined}
              onChange={e => { if (!pioneerSig) setDoseSig(e.target.value) }} placeholder="e.g. INFUSE 300 MG IV EVERY 6 MONTHS"
              title={pioneerSig ? 'The SIG comes from the anchored PioneerRx prescription — Pioneer is the source of truth and OwlRx never writes back.' : undefined}
              style={pioneerSig ? roInput : { padding: '6px 8px', borderRadius: 6, border: '1px solid var(--color-border)', fontSize: 12.5 }} />
          </label>
          {/* ASP 2026-10-11 (#3): the quantity sits beside the SIG (it drives refills later). Read-only from
              the anchored PioneerRx prescription, its fill's quantity on a refill — Pioneer owns it. */}
          <label style={{ fontSize: 11.5, color: 'var(--color-text-mute)', display: 'flex', flexDirection: 'column', gap: 2 }}>
            Qty (PioneerRx — read-only)
            <input readOnly tabIndex={-1} style={roInput}
              value={anchoredRx ? [anchoredRx.quantity, anchoredRx.days_supply ? `${anchoredRx.days_supply} days` : null].filter(Boolean).join(' · ') || 'Not on the prescription' : 'Pick a prescription'} />
          </label>

          {/* D-CRX-45 CR-G2 (priority set at enrollment, pharmacist-overridable later) + CR-G4
              (consultation-needed question — drives whether the Consultation panel shows in CR). */}
          <PharmacistPicker value={rphId} onChange={pickRph} />
          <label style={{ fontSize: 11.5, color: 'var(--color-text-mute)', display: 'flex', flexDirection: 'column', gap: 2 }}>
            Priority
            <SearchableSelect value={priority} onChange={(v: string) => setPriority(v)}
              options={[{ value: 'urgent', label: 'Urgent' }, { value: 'high', label: 'High' }, { value: 'normal', label: 'Normal' }, { value: 'low', label: 'Low' }]} />
          </label>
          <label style={{ fontSize: 11.5, color: 'var(--color-text-mute)', display: 'flex', flexDirection: 'column', gap: 2 }}>
            Consultation needed?
            <span style={{ display: 'inline-flex', gap: 10, alignItems: 'center', padding: '6px 0' }}>
              <label style={{ display: 'inline-flex', gap: 4, alignItems: 'center', fontSize: 12.5, color: 'var(--color-text)' }}>
                <input type="radio" name="consult-needed" checked={consultationNeeded} onChange={() => setConsultationNeeded(true)} /> Yes
              </label>
              <label style={{ display: 'inline-flex', gap: 4, alignItems: 'center', fontSize: 12.5, color: 'var(--color-text)' }}>
                <input type="radio" name="consult-needed" checked={!consultationNeeded} onChange={() => setConsultationNeeded(false)} /> No
              </label>
            </span>
          </label>
          <label style={{ fontSize: 11.5, color: 'var(--color-text-mute)', display: 'flex', flexDirection: 'column', gap: 2 }}>
            Dose (amount &amp; unit){pioneerDose ? ' (from Rx — read-only)' : ''}
            <input value={doseAmt} readOnly={!!pioneerDose} tabIndex={pioneerDose ? -1 : undefined}
              onChange={e => { if (!pioneerDose) setDoseAmt(e.target.value) }} placeholder="e.g. 300 MG IV"
              title={pioneerDose ? 'The dose derives from the Pioneer SIG — it cannot be changed here. Frequency is the adjustable half.' : undefined}
              style={pioneerDose ? roInput : { padding: '6px 8px', borderRadius: 6, border: '1px solid var(--color-border)', fontSize: 12.5 }} />
          </label>
          <label style={{ fontSize: 11.5, color: 'var(--color-text-mute)', display: 'flex', flexDirection: 'column', gap: 2 }}>
            Frequency{pioneerSig ? ' (from SIG — adjustable)' : ''}
            <input value={freq} onChange={e => setFreq(e.target.value)} placeholder="e.g. EVERY 6 MONTHS"
              style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid var(--color-border)', fontSize: 12.5 }} />
          </label>
          <label style={{ fontSize: 11.5, color: 'var(--color-text-mute)', display: 'flex', flexDirection: 'column', gap: 2 }}>
            Outreach status
          <SearchableSelect value={outreach} onChange={(v: string) => {
              setOutreach(v)
              // UAT 2026-08-11 (owner): the date matches the status — reaching the patient captures
              // the contact date (defaults to today, editable below).
              if (v === 'reached' && !outreachReachedAt) setOutreachReachedAt(new Date().toISOString().slice(0, 10))
            }}
            options={[{ value: '', label: 'Select…' }, { value: 'not_started', label: 'Not started' }, { value: 'in_progress', label: 'In progress' }, { value: 'reached', label: 'Reached' }, { value: 'unable_to_reach', label: 'Unable to reach' }]} />
          </label>
          {outreach === 'reached' && (
            <label style={{ fontSize: 11.5, color: 'var(--color-text-mute)', display: 'flex', flexDirection: 'column', gap: 2 }}>
              Date reached
              <DateInput label="Date reached" value={outreachReachedAt} onChange={v => setOutreachReachedAt(v ?? '')} />
            </label>)}
          {(outreach === 'unable_to_reach' || outreach === 'in_progress') && (
            <label style={{ fontSize: 11.5, color: 'var(--color-text-mute)', display: 'flex', flexDirection: 'column', gap: 2 }}>
              {outreach === 'unable_to_reach' ? <>Reach again on <span style={{ color: '#b91c1c' }}>*</span></> : <>Next attempt on</>}
              <DateInput label={outreach === 'unable_to_reach' ? 'Reach again on' : 'Next attempt on'} value={outreachNextAt}
                onChange={v => setOutreachNextAt(v ?? '')} emphasis={outreach === 'unable_to_reach' && !outreachNextAt ? 'overdue' : undefined} />
              {outreach === 'unable_to_reach' && !outreachNextAt &&
                <span style={{ color: '#b45309', fontSize: 11 }}>Required — when will the patient be reached again?</span>}
            </label>)}
          <label style={{ fontSize: 11.5, color: 'var(--color-text-mute)', display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* cite: v2.1 Part 3 Stage 1 "Next Infusion date" */}
            Next infusion date
            <DateInput label="Next infusion date" value={nextInf} onChange={v => setNextInf(v ?? '')} />
          </label>
          <label style={{ fontSize: 11.5, color: 'var(--color-text-mute)', display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* cite: v2.1 Part 3 Stage 1 "Deliver On date (warns if after Next Infusion...)" · H9 */}
            Deliver on
            <DateInput label="Deliver on" value={deliverOn} onChange={v => setDeliverOn(v ?? '')} />
            {(() => {
              // H9: the drug cannot arrive after the infusion it is for, and a past date is a typo.
              // Warnings only — never a block; the ruling says "warns".
              if (!deliverOn) return null
              const d = new Date(deliverOn + 'T00:00:00')
              if (nextInf && d > new Date(nextInf + 'T00:00:00'))
                return <span style={{ color: '#b45309', fontSize: 11 }}>Deliver On is AFTER the next infusion — the drug would arrive too late.</span>
              if (d < new Date(new Date().toISOString().slice(0, 10) + 'T00:00:00'))
                return <span style={{ color: '#b45309', fontSize: 11 }}>Deliver On is in the past.</span>
              return null
            })()}
          </label>
          {/* Gate G-5 (Patrick 2026-07-28): PA follow-up HIDDEN — enrollment starts only after PA
              approval, so the chase field has no place on the door. State + API field kept. */}
          <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Intake A notes"
            rows={2} style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid var(--color-border)', fontSize: 12.5, gridColumn: 'span 2', resize: 'vertical' }} />
        </div>
        {insName && <div style={{ fontSize: 12, color: 'var(--color-text-mute)' }}>Insurance: <b>{insName}</b> <span style={{ fontSize: 10.5 }}>(from Pioneer, display only)</span></div>}
        {/* D-CRX-72 — administration setup, above the labs block: protocols, flush and the rate
            rule as the drug record defines them, each adjustable for this patient. */}
        {formularyId && (
          <div style={{ margin: '14px 0 6px', paddingTop: 10, borderTop: '0.5px solid var(--color-border, #e5e7eb)' }}>
            <div style={{ fontSize: 13, fontWeight: 700 }}>Administration — protocols, flush, rate rule</div>
            <EnrollmentAdminConfig formularyId={formularyId} therapyId={editTherapy?.therapy_id}
              value={adminOv} onChange={setAdminOv} />
          </div>
        )}
        {(labQ.length > 0 || labWaived.length > 0 || labListEdit) && (<>
        <div style={{ margin: '14px 0 6px', paddingTop: 10, borderTop: '0.5px solid var(--color-border, #e5e7eb)' }}>
          <div style={{ fontSize: 13, fontWeight: 700 }}>Baseline labs — required for this drug</div>
          <div style={{ fontSize: 11.5, color: 'var(--color-text-soft, #6b7280)' }}>flat 12-month validity from the draw date · answers record the draw; Intake B verifies results</div>
        </div>
        {/* G-34: which lab provider will run the draws — from the Settings directory. */}
        <label style={{ fontSize: 11.5, color: 'var(--color-text-mute)', display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 340, marginBottom: 8 }}>
          Lab provider
          <SearchableSelect value={labProviderId} onChange={(v: string) => setLabProviderId(v)} ariaLabel="Lab provider"
            placeholder={labProviders.length ? 'Select lab provider…' : 'No lab providers — add in Pharmacy Settings → Lab providers'}
            options={labProviders.map(lp => { const dts = [lp.draw_walk_in && 'Walk In', lp.draw_scheduled && 'Scheduled', lp.draw_clinic && 'Clinic', lp.draw_phlebotomist && 'Phlebotomist', lp.draw_nurse && 'Nurse'].filter(Boolean).join(' · ')
              return { value: lp.id, label: `${lp.name}${dts ? ` — ${dts}` : ''}` } })} />
        </label>
        {(() => {
          const violated = shownLabQ.filter(q => { const a = labAns[q.analyte]
            if (q.current) return false
            if (a?.collected === 'no') return true
            if (a?.collected === 'yes' && a.noResults) return true
            if (a?.collected === 'yes' && (a.resultsOn || a.date)) {
              const d = new Date((a.resultsOn || a.date) + 'T00:00:00')
              return d < new Date(Date.now() - 365 * 864e5)
            }
            return false })
          if (violated.length === 0) return null
          return (
            <div style={{ fontSize: 12.5, color: '#7c4a03', background: '#fffbeb', border: '1px solid #d97706', borderRadius: 8, padding: '8px 11px', fontWeight: 600 }}>
              ⚠ Lab violation — {violated.map(q => q.analyte.replace(/_/g, ' ').toUpperCase()).join(', ')} {violated.length === 1 ? 'is' : 'are'} not current
              (not collected, no results, or results older than 12 months).
              <span style={{ fontWeight: 400 }}> Not a blocker — this enrollment saves as a draft and goes to the <b>Lab Tracker</b> before Clinical Review.</span>
            </div>) })()}
        {/* D-CRX-80: the shared LabPanel. This panel had NO bulk edit — a drug requiring twenty
            analytes meant twenty separate answers, and a batch drawn on one trip to the draw site
            shares its dates. It also labelled analytes its own way
            (analyte.replace(/_/g,' ').toUpperCase()), one of four labellings in the module. Both
            now come from the one component and the one vocabulary. Local state, not a write: this
            panel runs BEFORE the therapy exists, so answers still ride the create/edit payload. */}
        <LabPanel
          rows={shownLabQ.map(q => {
            const a = labAns[q.analyte] ?? { collected: '', date: '' }
            // D-CRX-95: an on-file lab is no longer a dead end — "Enter newer result" opens it for a new draw.
            const onFile = q.current && !labNewer.has(q.analyte)
            return {
              analyte: q.analyte,
              draw_status: onFile ? 'collected' : (a.collected === 'yes' ? 'collected' : 'pending'),
              collected_on: onFile ? (q.last_date ?? '') : a.date,
              results_on: onFile ? (q.last_date ?? '') : (a.resultsOn ?? ''),
              no_results: !!a.noResults,
              current: labVerdict(q) === 'current',
              locked: onFile,             // already on file — nothing to answer
              note: labReader ? labSourceNote(q, labNewer.has(q.analyte)) : (q.current ? `on file — last ${q.last_date}` : null),
            }
          })}
          rowActions={labReader ? (row => {
            const q = shownLabQ.find(x => x.analyte === row.analyte)
            if (!q) return null
            const local = !editTherapy && labAdded.some(x => x.analyte === row.analyte)
            return (
              <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 6, maxWidth: 170 }}>
                {q.current && (labNewer.has(q.analyte)
                  ? <LabAction onClick={() => reopenOnFile(q.analyte, false)}>Keep on-file result</LabAction>
                  : <LabAction title="A new draw or report: the on-file result stays on record"
                      onClick={() => reopenOnFile(q.analyte, true)}>Enter newer result</LabAction>)}
                {labListEdit && (local
                  ? <LabAction onClick={() => undoLocalLab(q.analyte)}>Remove</LabAction>
                  : isRph && <LabAction onClick={() => setLabDlg({ action: 'waive', analyte: q.analyte })}>Waive</LabAction>)}
              </span>)
          }) : undefined}
          editable={{ drawStatus: true, collectedOn: true, resultsOn: true, noResults: true }}
          drawStatusOptions={['pending', 'collected']}
          onEdit={(row, patch) => { setLabTouched(s => new Set(s).add(row.analyte)); setLabAns(m => {
            const a = m[row.analyte] ?? { collected: '', date: '' }
            const next = { ...a }
            if (patch.draw_status !== undefined)
              // Moving OFF collected clears the dates — a corrected mistake must stop counting as
              // evidence rather than leaving a date behind (same rule the tracker applies server-side).
              return patch.draw_status === 'collected'
                ? { ...m, [row.analyte]: { ...next, collected: 'yes' as const } }
                : { ...m, [row.analyte]: { collected: 'no' as const, date: '' } }
            if (patch.collected_on !== undefined) next.date = patch.collected_on ?? ''
            if (patch.results_on !== undefined) next.resultsOn = patch.results_on ?? ''
            if (patch.no_results !== undefined) { next.noResults = !!patch.no_results; if (patch.no_results) next.resultsOn = '' }
            return { ...m, [row.analyte]: next }
          }) }}
          onBulk={(rows, patch) => { setLabTouched(s => { const n = new Set(s); for (const r of rows) n.add(r.analyte); return n }); setLabAns(m => {
            const next = { ...m }
            for (const row of rows) {
              const a = next[row.analyte] ?? { collected: '' as const, date: '' }
              const upd = { ...a }
              if (patch.draw_status !== undefined) {
                if (patch.draw_status === 'collected') upd.collected = 'yes'
                else { next[row.analyte] = { collected: 'no', date: '' }; continue }
              }
              if (patch.collected_on !== undefined) upd.date = patch.collected_on ?? ''
              if (patch.results_on !== undefined) upd.resultsOn = patch.results_on ?? ''
              if (patch.no_results !== undefined) { upd.noResults = !!patch.no_results; if (patch.no_results) upd.resultsOn = '' }
              next[row.analyte] = upd
            }
            return next
          }) }}
          emptyText="No baseline labs required for this drug."
        />
        {labListEdit && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, margin: '6px 0' }}>
            <div><LabAction onClick={() => setLabDlg({ action: 'add' })}>+ Add lab</LabAction>
              {!isRph && <span style={{ fontSize: 11.5, color: 'var(--color-text-mute)', marginLeft: 8 }}>Waiving a lab needs a pharmacist.</span>}</div>
            {labWaived.map(w => (
              <div key={w.analyte} style={{ fontSize: 12.5, padding: '6px 10px', borderRadius: 8, border: '0.5px solid var(--color-border, #e5e7eb)', background: 'var(--color-surface-2, #f9fafb)' }}>
                <b>Waived:</b> {w.label ?? analyteLabel(w.analyte)} — {labReasonLabel(w.reason_code, w.reason_text)}
                {w.by ? ` · ${w.by}` : ''}{w.at ? ` · ${formatUsDateLocal(w.at)}` : ''}
                {!editTherapy
                  ? <span style={{ marginLeft: 8 }}><LabAction onClick={() => undoLocalLab(w.analyte)}>Undo</LabAction></span>
                  : isRph && <span style={{ marginLeft: 8 }}><LabAction onClick={() => setLabDlg({ action: 'restore', analyte: w.analyte })}>Restore</LabAction></span>}
              </div>))}
            {labNote && <div style={{ fontSize: 12, color: 'var(--color-text-mute)' }}>{labNote}</div>}
          </div>)}
        <LabRequirementDialog open={labDlg !== null} action={labDlg?.action ?? 'add'} analyte={labDlg?.analyte}
          exclude={[...labQ.map(q => q.analyte), ...labWaived.map(w => w.analyte)]}
          busy={labDlgBusy} error={labDlgErr} onClose={() => { setLabDlg(null); setLabDlgErr(null) }}
          onSubmit={item => labDlg && applyLabChange(labDlg.action, item)} />
        <div style={{ fontSize: 12, padding: '7px 10px', borderRadius: 8, border: '0.5px solid var(--color-border, #e5e7eb)', background: labMissing.length ? '#fffbeb' : '#ecfdf5', color: labMissing.length ? '#7c4a03' : '#065f46' }}>
          {labMissing.length
            ? <>Routing on create: <b>Lab Tracking</b> — {labMissing.length} lab(s) not current ({labMissing.map(q => q.analyte).join(', ')})</>
            : <>Routing on create: <b>Clinical Review</b> — all required labs current</>}
        </div>
        </>)}
        <div style={{ margin: '14px 0 6px', paddingTop: 10, borderTop: '0.5px solid var(--color-border, #e5e7eb)' }}>
          <div style={{ fontSize: 13, fontWeight: 700 }}>Site of care</div>
          <div style={{ fontSize: 11.5, color: 'var(--color-text-soft, #6b7280)' }}>required · facility per the type's own configuration · Home uses the patient address · other types use the facility's directory address</div>
        </div>
        {/* UAT 2026-07-29 (Patrick): aligned 2-col grid; scheduling City/Zip live HERE, with the
            mapped address (patient's for Home, facility's otherwise) — not under Enrollment details. */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, alignItems: 'end' }}>
          <label style={{ fontSize: 11.5, color: 'var(--color-text-mute)', display: 'flex', flexDirection: 'column', gap: 2 }}>
            Site of care <span style={{ color: '#b91c1c' }}>*</span>
          <SearchableSelect value={siteOfCare} onChange={(v: string) => setSiteOfCare(v)} ariaLabel="Site of care"
            options={socTypes.length > 0
              ? [{ value: '', label: 'Select…' }, ...socTypes.map(t => ({ value: t.code, label: t.label }))]
              : [{ value: '', label: 'Site of care…' }, { value: 'home', label: 'Home (CareRx nurse)' }, { value: 'infusion_center', label: 'Ambulatory Infusion Center' }, { value: 'clinic_site', label: 'Physician Clinic / Office' }]} />
          </label>
          <label style={{ fontSize: 11.5, color: 'var(--color-text-mute)', display: 'flex', flexDirection: 'column', gap: 2 }}>
            City (scheduling — Pioneer keeps the patient record)
            <input value={eCity} onChange={e => setECity(e.target.value)}
              style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid var(--color-border)', fontSize: 12.5 }} />
          </label>
          <label style={{ fontSize: 11.5, color: 'var(--color-text-mute)', display: 'flex', flexDirection: 'column', gap: 2 }}>
            Zip
            <input value={eZip} onChange={e => setEZip(e.target.value)} maxLength={10}
              style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid var(--color-border)', fontSize: 12.5 }} />
          </label>
          <div />
          {/* B3: Home uses the patient address — show it read-only from the Pioneer banner. */}
          {socType && !socType.requires_facility && (
            <label style={{ fontSize: 11.5, color: 'var(--color-text-mute)', display: 'flex', flexDirection: 'column', gap: 2, gridColumn: 'span 2', marginTop: 4 }}>
              Patient address (PioneerRx — read-only · the {socType.label} visit happens here)
              <input readOnly tabIndex={-1}
                value={rxBanner?.address_line
                  ? `${rxBanner.address_line}, ${rxBanner.city ?? ''}, ${rxBanner.state_code ?? ''} ${rxBanner.zip_code ?? ''}`.replace(/, ,/g, ',')
                  : 'No address on file (PioneerRx owns the patient address)'}
                style={{ padding: '8px 10px', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 13,
                         background: 'var(--color-surface-2, #f9fafb)', fontWeight: rxBanner?.address_line ? 600 : 400,
                         color: rxBanner?.address_line ? 'var(--color-text)' : 'var(--color-text-mute)' }} />
            </label>)}
          {socType?.requires_facility && (
            <div style={{ gridColumn: 'span 2' }}>
              <SearchableSelect value={facilityId} onChange={(v: string) => setFacilityId(v)} ariaLabel="Facility"
                options={[{ value: '', label: `Facility (required for ${socType.label.split(' — ')[0]})…` },
                  ...facilities.map(facilityOption)]} menuMinWidth={520} />
              {facilities.length === 0 && (
                <div style={{ fontSize: 11.5, color: 'var(--color-warning-fg, #92400e)', marginTop: 4 }}>
                  No active {socType.label.split(' — ')[0]} facility in the directory — add one in Pharmacy Settings → Site of Care.
                </div>)}
              {facilityId && (() => { const f = facilities.find(x => x.id === facilityId); return f?.address_line
                ? <div style={{ fontSize: 11.5, color: 'var(--color-text-mute, #9ca3af)', marginTop: 4 }}>{f.address_line}, {f.city}, {f.state_code} {f.zip_code} · address travels with the facility record</div> : null })()}
            </div>)}
        </div>
        {/* R-18 RULED (Patrick 2026-07-30): Home Medications REMOVED from the enrollment —
            allergies from Pioneer (banner + PDF §03) replace it here. The carerx_home_meds model,
            API, and Pioneer push ledger (D-CRX-13) remain for the patient chart; Master test A4
            retires from the door script. */}
        {twins.length > 0 && (
          <div style={{ background: '#fffbeb', border: '1px solid #d97706', borderRadius: 8, padding: 10, fontSize: 12.5 }}>
            <b>⚠ Twin check (§3.1):</b> {twins.map(t => t.display_name ?? 'patient').join(', ')} shares this last name + DOB.
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 6 }}>
              <input type="checkbox" checked={twinVerified} onChange={e => setTwinVerified(e.target.checked)} />
              I have verified this is the correct patient
            </label>
          </div>
        )}
        {dups.length > 0 && (
          <div style={{ background: '#fef2f2', border: '1px solid #dc2626', borderRadius: 8, padding: 10, fontSize: 12.5 }}>
            <b>Open therapy exists</b> ({dups.map(d => d.state).join(', ')}). To start a NEW one anyway, give a reason:
            <input value={overrideReason} onChange={e => setOverrideReason(e.target.value)} placeholder="Override reason (audited)"
              style={{ width: '100%', marginTop: 6, padding: '6px 8px', borderRadius: 6, border: '1px solid var(--color-border)', fontSize: 12.5 }} />
          </div>
        )}
        {err && <div role="alert" style={{ fontSize: 12.5, color: '#b91c1c' }}>{err}</div>}
        <div style={{ display: 'flex', gap: 8 }}>
          {isEdit
            ? <button disabled={busy || !patient || !rphId} onClick={() => submit(false)} style={{ ...linkBtn, fontWeight: 700 }}>{busy ? 'Saving…' : 'Save changes'}</button>
            : dups.length === 0
            ? <button disabled={busy || !patient || !formularyId || !rphId} onClick={() => submit(false)} style={{ ...linkBtn, fontWeight: 700 }}>{busy ? 'Creating…' : 'Create enrollment'}</button>
            : <button disabled={busy || !overrideReason.trim() || !rphId} onClick={() => submit(true)} style={{ ...linkBtn, fontWeight: 700 }}>{busy ? 'Creating…' : 'Create anyway (audited)'}</button>}
          <button onClick={onClose} style={linkBtn}>Cancel</button>
        </div>
      </>)}
    </div>
  )
}

/**
 * Supply readiness strip (round table 2026-08-31). Shows the anchor drug's supply-generator
 * profile: approval state, the label-sourced facts pick lists will run on, and the saved-list
 * count. STRICTLY read-only and fail-open — a missing profile, an unlinked drug, a 404 or any
 * server error renders NOTHING; enrollment never blocks on supply. Facts are attributed
 * "(Drug Reference)" so nothing here reads as a patient order (Andrew).
 */
function SupplyReadinessStrip({ formularyId }: { formularyId: string }) {
  const [sum, setSum] = useState<api.SupplyProfileSummary | null>(null)
  useEffect(() => {
    setSum(null)
    if (!formularyId || formularyId.startsWith('unlinked:')) return
    let cancelled = false
    api.getCareRxSupplyProfileSummary(formularyId)
      .then(s => { if (!cancelled) setSum(s) })
      .catch(() => { /* fail-open: strip absent, enrollment untouched */ })
    return () => { cancelled = true }
  }, [formularyId])

  if (!sum) return null
  if (!sum.approved) return (
    <div style={{ fontSize: 11.5, color: 'var(--color-text-mute)', marginTop: 3 }}>
      Supply profile: <b>not approved yet</b> <span style={{ opacity: .7 }}>(Drug Reference)</span> — pick lists cannot generate for this drug until a pharmacist approves it.
    </div>
  )

  const chip = { fontSize: 11, background: 'var(--color-surface, #fff)', border: '1px solid var(--color-border, #d8e4ec)', borderRadius: 10, padding: '2px 9px' }
  return (
    <div style={{ border: '1px solid #b9e3c3', background: '#f4fbf5', borderRadius: 8, padding: '9px 12px', marginTop: 6 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 700, color: '#0a7a1d' }}>
        ✓ Supply readiness — profile approved{sum.approved_at ? ` · ${new Date(sum.approved_at).toLocaleDateString()}` : ''} · rules v{sum.rules_version} <span style={{ fontWeight: 400, opacity: .75 }}>(Drug Reference)</span>
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
        {sum.supply_route && <span style={chip}>route {sum.supply_route}{sum.infusion_duration_min ? ` · ${sum.infusion_duration_min} min infusion` : ''}</span>}
        {sum.diluent && <span style={chip}>{sum.diluent}{sum.standard_final_volume_ml ? ` · ${sum.standard_final_volume_ml} mL` : ''}{sum.final_conc_max_mg_ml ? ` · ${sum.final_conc_min_mg_ml ?? '?'}–${sum.final_conc_max_mg_ml} mg/mL` : ''}</span>}
        {sum.filter_min_micron != null && <span style={chip}>filter {sum.filter_min_micron}–{sum.filter_max_micron} micron</span>}
        {sum.rescue_kit_required && <span style={chip}>rescue kit required</span>}
        {!sum.rescue_kit_required && sum.rescue_override_reason && <span style={{ ...chip, background: '#fef6e7', borderColor: '#f3d9a4' }}>rescue kit waived: {sum.rescue_override_reason}</span>}
        {!sum.co_infusion_allowed && sum.supply_route === 'IV' && <span style={chip}>no co-infusion</span>}
      </div>
      <div style={{ fontSize: 11, color: 'var(--color-text-soft, #4b5a68)', marginTop: 6 }}>
        Drug-profile facts, not patient orders. Pick lists generate per visit from this profile using the visit&#39;s pump and site of care{sum.saved_list_count > 0 ? ` — ${sum.saved_list_count} saved list${sum.saved_list_count === 1 ? '' : 's'} for this drug` : ''}.
      </div>
    </div>
  )
}
